import { UserAdminItem } from "../interfaces/UserAdminItem";
import { Excel2UserItem } from "../interfaces/Excel2UserItem";
import { expandExcel2Privileges } from "./Excel2Privileges";
import { LogInItem } from "../interfaces/LogInItem";
import { DomainItem } from "../interfaces/DomainItem";
import { APIItem } from "../interfaces/APIItem";

// The portal works off a single userData: UserAdminItem (menu, routing, domain picker
// all read it). The unified admin_login endpoint instead returns two independent
// entries - User (users.json) and Excel2User (Excel2Users.json) - either of which may
// be absent. This flattens a LogInItem into the one UserAdminItem the portal expects:
//
//   - User present  -> start from it. Otherwise synthesise a minimal UserAdminItem from
//     Excel2User so an Excel2/ESEF-only account can still sign in; all users.json
//     privilege flags stay false (those privileges live on Excel2User, read separately
//     from context.excel2User).
//   - Domains / API = the union of both sides (deduped), so the account sees all its
//     domains regardless of which table granted them.
//   - isExcel2Admin = User.isAdmin || Excel2User.isExcel2Admin, the flag that gates the
//     Excel2 Accounts view. ESEF privileges never reach it - an ESEF Admin does not
//     administer Excel2 accounts.
//   - isExcel2WebAdmin / isExcel2WordAdmin / isESEFAdmin / isESEFTaxonomist are
//     OVERWRITTEN with the inheritance-expanded Excel2User capabilities, so
//     Excel2Users.json is the only source for the Excel2 and ESEF tracks. Whatever
//     users.json said about those four no longer gates anything, and with no Excel2
//     entry they are all false. admin_login synthesises a fully-privileged Excel2
//     entry for an isAdmin account, so a super admin still keeps every track.
//   - Change tenant follows User when present, otherwise Excel2User.

const dedupeDomains = (domains: DomainItem[]): DomainItem[] => {
	const seen = new Set<string>();
	const result: DomainItem[] = [];
	for (const d of domains) {
		if (!d || !d.DomainName) continue;
		const key = `${d.DomainName.toLowerCase()}|${(d.APIName || '').toLowerCase()}`;
		if (seen.has(key)) continue;
		seen.add(key);
		result.push(d);
	}
	return result;
};

const dedupeApis = (apis: APIItem[]): APIItem[] => {
	const seen = new Set<string>();
	const result: APIItem[] = [];
	for (const a of apis) {
		if (!a || !a.APIName) continue;
		const key = a.APIName.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		result.push(a);
	}
	return result;
};

// A UserAdminItem with all users.json privilege flags off, for an Excel2-only account.
const emptyUserAdminItem = (user: string): UserAdminItem => ({
	User: user,
	Domains: [],
	API: [],
	changeTenantDomain: '',
	changeTenantEnabled: false,
	isAdmin: false,
	isSettingsAdmin: false,
	isContributorAdmin: false,
	isSectionsAdmin: false,
	isTemplateAdmin: false,
	isPictureAdmin: false,
	isRehngruppen: false,
	isIconAdmin: false,
	isLogoAdmin: false,
	isSlideAdmin: false,
	isContentAdmin: false,
	isBuilderAdmin: false,
	isTemplateDefAdmin: false,
	isPictureDefAdmin: false,
	isEmailSignatureAdmin: false,
	isAcademyAdmin: false,
	isExcel2WebAdmin: false,
	isExcel2WordAdmin: false,
	isESEFAdmin: false,
	isAIAdmin: false,
	blockRehngruppenAdmin: false,
	isExcel2Admin: false,
});

export const buildUserData = (login: LogInItem): UserAdminItem => {
	const user: UserAdminItem | null | undefined = login.User;
	const excel2User: Excel2UserItem | null | undefined = login.Excel2User;

	const base: UserAdminItem = user
		? { ...user }
		: emptyUserAdminItem(excel2User?.User || '');

	// Union of domains/API from both tables.
	base.Domains = dedupeDomains([...(user?.Domains || []), ...(excel2User?.Domains || [])]);
	base.API = dedupeApis([...(user?.API || []), ...(excel2User?.API || [])]);

	// isRehngruppen from whichever side reports it.
	base.isRehngruppen = !!(user?.isRehngruppen || excel2User?.isRehngruppen);

	// Change tenant: users.json wins when present, else the Excel2 entry's own answer.
	if (!user && excel2User) {
		base.changeTenantEnabled = excel2User.changeTenantEnabled;
		base.changeTenantDomain = excel2User.changeTenantDomain;
	}

	// Gate for the Excel2 Accounts view. isAdmin overrides everything.
	base.isExcel2Admin = !!(user?.isAdmin || excel2User?.isExcel2Admin);

	// The Excel2 and ESEF tracks are read off Excel2Users.json alone, with the
	// inheritance expanded here rather than in the menu. No Excel2 entry means no
	// Excel2 and no ESEF, whatever users.json happens to say.
	const caps = excel2User ? expandExcel2Privileges(excel2User) : null;
	base.isExcel2WebAdmin = !!caps?.excel2Web;
	base.isExcel2WordAdmin = !!caps?.excel2Word;
	base.isESEFAdmin = !!caps?.esefAdmin;
	base.isESEFTaxonomist = !!caps?.esefTaxonomist;

	return base;
};
